import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { extrairIncidenteViaN8n } from '../services/incidentesApi';
import { useOrion } from '../context/OrionContext';
import { separarExecutavelData } from '../services/executavelData';
import { printsDoAtendimento, resumoDaMigracao } from '../services/printsIncidente';

// ─────────────────────────────────────────────────────────────────────────────
// Do atendimento para o incidente.
//
// Registrar um incidente logo depois de fechar o ticket significava reescrever
// tudo: o que aconteceu, o executável, a versão, e anexar os prints de novo —
// os mesmos que acabaram de ser colados na formalização. Na prática, quase
// ninguém registrava.
//
// Esta tela faz a ponte: pede à IA que leia o atendimento, separa o executável
// da data, converte os prints e entrega o formulário preenchido.
//
// O modal existe porque isso leva segundos. Sem retorno visual o operador olha
// uma tela parada e clica de novo. Cada passo mostra ⏳ enquanto roda, ✅ quando
// dá certo e ⚠️ quando falha — e **falhar não interrompe**: o formulário abre
// do mesmo jeito, para preenchimento manual. A IA fora do ar não pode ser
// motivo para o incidente não ser registrado.
// ─────────────────────────────────────────────────────────────────────────────

const PASSOS = [
  { id: 'ia', rotulo: 'Analisando o atendimento com a IA' },
  { id: 'form', rotulo: 'Preenchendo o formulário do incidente' },
  { id: 'prints', rotulo: 'Levando os prints do atendimento' },
];

export default function TransicaoIncidente({
  numeroTicket,
  solicitante,
  anotacoes,
  textoFinal,
  imagens = [],
  statusInicial = 'pendente',
  gut = null,
  aoPronto,      // (pacote) => void
  aoCancelar,
}) {
  // O operador acompanha a extração: o fluxo do 1.4.1 recebe nome e e-mail.
  const { operador } = useOrion();

  const [estado, setEstado] = useState(() =>
    Object.fromEntries(PASSOS.map(p => [p.id, { situacao: 'espera', detalhe: '' }])));
  const jaRodou = useRef(false);

  useEffect(() => {
    if (jaRodou.current) return;
    jaRodou.current = true;

    const marcar = (id, situacao, detalhe = '') =>
      setEstado(e => ({ ...e, [id]: { situacao, detalhe } }));

    (async () => {
      // ── 1. a IA lê o atendimento ──
      marcar('ia', 'andando');
      let extraido = null;
      try {
        const r = await extrairIncidenteViaN8n(solicitante, anotacoes, textoFinal, numeroTicket, operador);
        extraido = Array.isArray(r) ? r[0] : r;
        marcar('ia', 'ok', 'campos sugeridos a partir do que foi escrito');
      } catch (e) {
        // Sem IA o formulário abre vazio — é pior, mas não impede nada.
        marcar('ia', 'falhou', 'preencha à mão; o resto segue normalmente');
      }

      // ── 2. os campos ──
      marcar('form', 'andando');
      const bruto = extraido || {};
      const { executavel, data } = separarExecutavelData(
        bruto.executavel || bruto.executavel_versao || '',
        bruto.executavel_data || '',
      );
      const preenchimento = {
        titulo: bruto.erro || bruto.titulo || bruto.resumo || '',
        detalhe: bruto.detalhe || bruto.detalhes || '',
        paliativa: bruto.solucao_paliativa || bruto.paliativa || '',
        sistema: bruto.sistema || '',
        executavel,
        executavelData: data,
      };
      marcar('form', 'ok', executavel && data
        ? `executável ${executavel}, de ${data.split('-').reverse().join('/')}`
        : 'campos prontos para revisão');

      // ── 3. os prints ──
      marcar('prints', 'andando');
      let prints = [];
      try {
        const r = await printsDoAtendimento(imagens);
        prints = r.prints;
        marcar('prints', r.prints.length ? 'ok' : 'espera', resumoDaMigracao(r));
      } catch (e) {
        marcar('prints', 'falhou', 'anexe as telas no formulário');
      }

      // O último passo fica visível por um instante antes de a tela trocar —
      // senão o modal pisca e ninguém lê o que aconteceu.
      setTimeout(() => {
        aoPronto({ numeroTicket, preenchimento, prints, statusInicial, gut });
      }, 900);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const icone = (situacao) => {
    if (situacao === 'andando') return <Icon name="rotate-cw" size={14} className="orion-spin" />;
    if (situacao === 'ok') return <Icon name="check" size={14} />;
    if (situacao === 'falhou') return <Icon name="alert-triangle" size={14} />;
    return <Icon name="circle" size={14} />;
  };

  return (
    <div className="drawer-overlay">
      <div className="modal-nx" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            Preparando o registro do incidente
            <span className="drawer-sub">ticket {numeroTicket}</span>
          </div>
        </div>

        <div className="drawer-body">
          <ul className="tr-passos">
            {PASSOS.map(p => (
              <li key={p.id} className={`tr-passo tr-${estado[p.id].situacao}`}>
                <span className="tr-ic">{icone(estado[p.id].situacao)}</span>
                <span className="tr-texto">
                  {p.rotulo}
                  {estado[p.id].detalhe && <span className="tr-detalhe">{estado[p.id].detalhe}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="drawer-foot">
          <button className="btn btn-ghost btn-sm" onClick={aoCancelar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
